// Tiny inline markdown renderer for product content (descriptions, terms, etc).
// Supports: **bold**, *italic*, line breaks, numbered lists (1. ...), bullet lists (- ...).
// Output: array of React nodes. Safe — no HTML injection.
import React from 'react';

const inlineFormat = (text, keyPrefix = 'i') => {
    // Process **bold** then *italic*; keep order to avoid greedy clashes
    const tokens = [];
    let i = 0;
    let buffer = '';
    const flush = () => {
        if (buffer) {
            tokens.push(buffer);
            buffer = '';
        }
    };

    while (i < text.length) {
        const rest = text.slice(i);
        // **bold**
        const bold = rest.match(/^\*\*([^*][^*]*?)\*\*/);
        if (bold) {
            flush();
            tokens.push(<strong key={`${keyPrefix}-b-${tokens.length}`}>{bold[1]}</strong>);
            i += bold[0].length;
            continue;
        }
        // *italic*
        const italic = rest.match(/^\*([^*\n][^*\n]*?)\*/);
        if (italic) {
            flush();
            tokens.push(<em key={`${keyPrefix}-i-${tokens.length}`}>{italic[1]}</em>);
            i += italic[0].length;
            continue;
        }
        buffer += text[i];
        i++;
    }
    flush();
    return tokens;
};

/**
 * Convert markdown text → array of React block elements.
 * Block types: p, ul, ol
 */
export const renderMarkdown = (text, options = {}) => {
    if (!text || typeof text !== 'string') return null;
    const lines = text.split('\n');
    const blocks = [];
    let listType = null; // 'ul' | 'ol' | null
    let listItems = [];

    const flushList = () => {
        if (listItems.length > 0) {
            const Tag = listType;
            blocks.push(
                <Tag key={`list-${blocks.length}`} className={listType === 'ul' ? 'list-disc pl-5 space-y-0.5' : 'list-decimal pl-5 space-y-0.5'}>
                    {listItems.map((li, idx) => (
                        <li key={`li-${idx}`}>{inlineFormat(li, `li-${blocks.length}-${idx}`)}</li>
                    ))}
                </Tag>
            );
            listItems = [];
            listType = null;
        }
    };

    lines.forEach((rawLine, idx) => {
        const line = rawLine.trimEnd();
        const ulMatch = line.match(/^\s*-\s+(.*)$/);
        const olMatch = line.match(/^\s*\d+\.\s+(.*)$/);
        if (ulMatch) {
            if (listType && listType !== 'ul') flushList();
            listType = 'ul';
            listItems.push(ulMatch[1]);
            return;
        }
        if (olMatch) {
            if (listType && listType !== 'ol') flushList();
            listType = 'ol';
            listItems.push(olMatch[1]);
            return;
        }
        flushList();
        if (line.trim() === '') {
            blocks.push(<div key={`sp-${idx}`} className="h-2" />);
        } else {
            blocks.push(
                <p key={`p-${idx}`} className="whitespace-pre-wrap">{inlineFormat(line, `p-${idx}`)}</p>
            );
        }
    });
    flushList();

    return blocks;
};

/**
 * Convert markdown → reportlab/HTML-safe inline string (for backend PDFs).
 * Keeps **bold** → <b>, *italic* → <i>, and converts \n to <br/>.
 * NOT used directly from frontend; the backend mirrors this logic in server.py.
 */
export const markdownToInlineHtml = (text) => {
    if (!text) return '';
    let s = String(text);
    s = s.replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>');
    s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<i>$2</i>');
    s = s.replace(/\n/g, '<br/>');
    return s;
};

export default renderMarkdown;
